import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  LayoutDashboard,
  Pencil,
  Percent,
  Plus,
  Save,
  Search,
  Ticket,
  Trash2,
  XCircle,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("feral_admin_token")}` },
});

const inputCls =
  "w-full bg-white border border-[#d9c3a8] px-3 py-2.5 rounded-sm outline-none focus:ring-2 focus:ring-[#ce8a39] text-[#1e231f] text-sm";

const blankDiscount = () => ({
  code: "",
  type: "percent",
  value: 10,
  active: true,
  appliesTo: "all",
  eventScope: "selected",
  eventSlugs: [],
  startsAt: "",
  expiresAt: "",
  usageLimit: null,
  label: "",
});

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-[#7a6a58] mb-1">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-[#9a876f] mt-1">{hint}</span>}
  </label>
);

const toLocalDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};

const discountValueLabel = (item) => {
  const value = Number(item.value || 0);
  if (item.type === "fixed") {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  return `${Number.isInteger(value) ? value : value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}%`;
};

const discountStatus = (item) => {
  if (item.active === false) return { label: "Inactive", tone: "muted" };
  const now = Date.now();
  const starts = item.startsAt ? new Date(item.startsAt).getTime() : null;
  const expires = item.expiresAt ? new Date(item.expiresAt).getTime() : null;
  if (starts && starts > now) return { label: "Scheduled", tone: "scheduled" };
  if (expires && expires <= now) return { label: "Expired", tone: "expired" };
  if (item.usageLimit && Number(item.usedCount || 0) >= Number(item.usageLimit)) {
    return { label: "Limit reached", tone: "expired" };
  }
  return { label: "Active", tone: "active" };
};

const statusCls = {
  active: "bg-[#e5efe3] text-[#42613e] border-[#bfd0bb]",
  scheduled: "bg-[#f7edd9] text-[#8b5d20] border-[#e5c996]",
  expired: "bg-[#f6e2dc] text-[#9b3d0a] border-[#e6b9aa]",
  muted: "bg-[#eee9e1] text-[#786d61] border-[#d8cec1]",
};

const AdminDiscounts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [discounts, setDiscounts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState(searchParams.get("event") || "all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const guard = useCallback(
    (e) => {
      if (e?.response?.status === 401) {
        localStorage.removeItem("feral_admin_token");
        localStorage.removeItem("feral_admin_user");
        navigate("/admin/login");
      }
    },
    [navigate]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [discountRes, eventRes] = await Promise.all([
        axios.get(`${API}/admin/discounts`, authHeaders()),
        axios.get(`${API}/admin/events`, authHeaders()),
      ]);
      setDiscounts(discountRes.data || []);
      setEvents((eventRes.data || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch (e) {
      guard(e);
      setMsg(e?.response?.data?.detail || "Could not load discounts.");
    } finally {
      setLoading(false);
    }
  }, [guard]);

  useEffect(() => {
    if (!localStorage.getItem("feral_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  useEffect(() => {
    setEventFilter(searchParams.get("event") || "all");
  }, [searchParams]);

  const eventNames = useMemo(
    () => Object.fromEntries(events.map((event) => [event.slug, event.name || event.slug])),
    [events]
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return discounts.filter((item) => {
      const status = discountStatus(item).label.toLowerCase();
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (eventFilter !== "all") {
        const coversEvent = item.eventScope === "all" || (item.eventSlugs || []).includes(eventFilter);
        if (!coversEvent) return false;
      }
      if (!needle) return true;
      const eventText =
        item.eventScope === "all"
          ? "all events site wide"
          : (item.eventSlugs || []).map((slug) => eventNames[slug] || slug).join(" ");
      return [item.code, item.label, item.appliesTo, eventText]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [discounts, eventFilter, eventNames, search, statusFilter]);

  const startNew = () => {
    setEditingId(null);
    const next = blankDiscount();
    if (eventFilter !== "all") next.eventSlugs = [eventFilter];
    setDraft(next);
    setMsg("");
    setView("edit");
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft({
      ...blankDiscount(),
      ...JSON.parse(JSON.stringify(item)),
      eventSlugs: [...(item.eventSlugs || [])],
    });
    setMsg("");
    setView("edit");
  };

  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const toggleEvent = (slug) => {
    setDraft((current) => {
      const selected = new Set(current.eventSlugs || []);
      if (selected.has(slug)) selected.delete(slug);
      else selected.add(slug);
      return { ...current, eventSlugs: [...selected] };
    });
  };

  const save = async () => {
    setMsg("");
    if (!draft.code.trim()) {
      setMsg("Discount code is required.");
      return;
    }
    if (draft.eventScope === "selected" && !(draft.eventSlugs || []).length) {
      setMsg("Choose at least one event, or change event scope to All events.");
      return;
    }

    const payload = {
      discount: {
        ...draft,
        code: draft.code.trim().toUpperCase(),
        value: Number(draft.value) || 0,
        usageLimit: draft.usageLimit ? Number(draft.usageLimit) : null,
        eventSlugs: draft.eventScope === "all" ? [] : draft.eventSlugs || [],
      },
    };

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/discounts/${editingId}`, payload, authHeaders());
      } else {
        await axios.post(`${API}/admin/discounts`, payload, authHeaders());
      }
      await load();
      setView("list");
      setDraft(null);
      setEditingId(null);
    } catch (e) {
      guard(e);
      setMsg(e?.response?.data?.detail || "Could not save discount.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/discounts/${deleteTarget.id}`, authHeaders());
      setDeleteTarget(null);
      await load();
    } catch (e) {
      guard(e);
      setMsg(e?.response?.data?.detail || "Could not delete discount.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4efe5]" data-testid="admin-discounts">
      <header className="bg-[#1c3c38] text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="feral-serif text-2xl">Discounts</h1>
            <p className="text-white/60 text-xs">Manage every checkout code in one place.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm transition-colors"
            >
              <LayoutDashboard size={16} /> Dashboard
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/events")}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm transition-colors"
            >
              <Ticket size={16} /> Events
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {view === "list" ? (
          <>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="feral-serif text-[#9b3d0a] text-2xl">Discount codes</h2>
                <p className="text-[#6e5646] text-sm mt-1">
                  Create event-specific or site-wide codes. Site-wide codes also apply to future events.
                </p>
              </div>
              <button
                type="button"
                onClick={startNew}
                data-testid="new-discount-button"
                className="inline-flex items-center justify-center gap-2 bg-[#9b3d0a] hover:bg-[#7e3208] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
              >
                <Plus size={16} /> Add discount
              </button>
            </div>

            <div className="bg-white border border-[#e0d3bf] rounded-lg p-4 mb-5 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9a876f]" />
                <input
                  className={`${inputCls} pl-9`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search code, event or label"
                />
              </div>
              <select
                className={`${inputCls} md:w-64`}
                value={eventFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setEventFilter(value);
                  const next = new URLSearchParams(searchParams);
                  if (value === "all") next.delete("event");
                  else next.set("event", value);
                  setSearchParams(next, { replace: true });
                }}
              >
                <option value="all">All events</option>
                {events.map((event) => (
                  <option key={event.slug} value={event.slug}>{event.name || event.slug}</option>
                ))}
              </select>
              <select
                className={`${inputCls} md:w-48`}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="scheduled">Scheduled</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
                <option value="limit reached">Limit reached</option>
              </select>
            </div>

            {msg && (
              <p className="mb-5 text-[#9b3d0a] text-sm bg-[#f3e7d6] border border-[#e0c9ad] rounded px-3 py-2">
                {msg}
              </p>
            )}

            {loading ? (
              <div className="bg-white border border-[#e0d3bf] rounded-lg p-8 text-center text-[#7a6a58]">Loading discounts…</div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-[#e0d3bf] rounded-lg p-8 text-center">
                <Percent size={28} className="mx-auto text-[#ce8a39] mb-3" />
                <p className="text-[#1e231f] font-semibold">No discounts found</p>
                <p className="text-[#7a6a58] text-sm mt-1">Add a discount or change the current filters.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((item) => {
                  const status = discountStatus(item);
                  const eventLabel =
                    item.eventScope === "all"
                      ? "All events"
                      : (item.eventSlugs || []).map((slug) => eventNames[slug] || slug).join(", ");
                  const used = Number(item.usedCount || 0);
                  return (
                    <div
                      key={item.id}
                      className="bg-white border border-[#e0d3bf] rounded-lg p-5"
                      data-testid={`discount-row-${item.id}`}
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_0.8fr_0.8fr_auto] gap-4 lg:items-center">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="feral-serif text-[#9b3d0a] text-xl">{item.code}</span>
                            <span className={`border rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusCls[status.tone]}`}>
                              {status.label}
                            </span>
                          </div>
                          <p className="text-[#6e5646] text-sm mt-1 truncate">{item.label || "No receipt label"}</p>
                        </div>

                        <div>
                          <p className="text-[11px] uppercase tracking-[0.1em] text-[#9a876f] font-semibold">Events</p>
                          <p className="text-[#1e231f] text-sm mt-1">{eventLabel || "No events"}</p>
                        </div>

                        <div>
                          <p className="text-[11px] uppercase tracking-[0.1em] text-[#9a876f] font-semibold">Discount</p>
                          <p className="text-[#1e231f] text-sm font-semibold mt-1">
                            {discountValueLabel(item)} · {item.appliesTo === "all" ? "All items" : item.appliesTo === "tickets" ? "Tickets" : "Accommodation"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[11px] uppercase tracking-[0.1em] text-[#9a876f] font-semibold">Usage</p>
                          <p className="text-[#1e231f] text-sm mt-1">
                            {used}{item.usageLimit ? ` / ${item.usageLimit}` : " uses"}
                          </p>
                        </div>

                        <div className="flex items-center gap-2 lg:justify-end">
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            className="inline-flex items-center gap-1.5 text-[#9b3d0a] hover:bg-[#f3e7d6] px-3 py-2 rounded-full text-sm font-semibold"
                          >
                            <Pencil size={14} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(item)}
                            className="inline-flex items-center justify-center w-9 h-9 text-[#9b3d0a] hover:bg-[#f6e2dc] rounded-full"
                            aria-label={`Delete ${item.code}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>

                      {(item.startsAt || item.expiresAt) && (
                        <div className="mt-4 pt-3 border-t border-[#eee3d2] flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#7a6a58]">
                          {item.startsAt && <span>Starts {formatDate(item.startsAt)}</span>}
                          {item.expiresAt && <span>Expires {formatDate(item.expiresAt)}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="max-w-4xl mx-auto">
            <button
              type="button"
              onClick={() => { setView("list"); setDraft(null); setMsg(""); }}
              className="inline-flex items-center gap-2 text-[#6e5646] text-sm font-semibold mb-5"
            >
              <ArrowLeft size={16} /> Back to discounts
            </button>

            <div className="bg-white border border-[#e0d3bf] rounded-lg p-6 md:p-7">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
                <div>
                  <h2 className="feral-serif text-[#9b3d0a] text-2xl">{editingId ? "Edit discount" : "Add discount"}</h2>
                  <p className="text-[#7a6a58] text-sm mt-1">Checkout validates these settings on the server.</p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-[#1e231f] font-semibold">
                  <input
                    type="checkbox"
                    checked={draft?.active !== false}
                    onChange={(e) => set("active", e.target.checked)}
                  />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Code">
                  <input
                    className={inputCls}
                    value={draft?.code || ""}
                    placeholder="FERAL10"
                    onChange={(e) => set("code", e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
                  />
                </Field>

                <Field label="Discount type">
                  <select className={inputCls} value={draft?.type || "percent"} onChange={(e) => set("type", e.target.value)}>
                    <option value="percent">Percentage</option>
                    <option value="fixed">Fixed £ amount</option>
                  </select>
                </Field>

                <Field label={draft?.type === "fixed" ? "Amount (£)" : "Percent (%)"}>
                  <input
                    type="number"
                    min="0"
                    max={draft?.type === "percent" ? "100" : undefined}
                    step={draft?.type === "fixed" ? "0.01" : "1"}
                    className={inputCls}
                    value={draft?.value ?? 0}
                    onChange={(e) => set("value", e.target.value)}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <Field label="Applies to">
                  <select className={inputCls} value={draft?.appliesTo || "all"} onChange={(e) => set("appliesTo", e.target.value)}>
                    <option value="all">All booking items</option>
                    <option value="tickets">Tickets only</option>
                    <option value="accommodation">Accommodation only</option>
                  </select>
                </Field>

                <Field label="Event scope" hint={draft?.eventScope === "all" ? "This code will also work on events created later." : "Choose one or more current events below."}>
                  <select
                    className={inputCls}
                    value={draft?.eventScope || "selected"}
                    onChange={(e) => set("eventScope", e.target.value)}
                  >
                    <option value="selected">Selected events</option>
                    <option value="all">All events, including future events</option>
                  </select>
                </Field>
              </div>

              {draft?.eventScope === "selected" && (
                <div className="mt-5 border border-[#e0d3bf] bg-[#faf6ee] rounded-md p-4">
                  <p className="text-xs font-semibold text-[#7a6a58] mb-3">Choose events</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {events.map((event) => (
                      <label key={event.slug} className="flex items-start gap-2 bg-white border border-[#e7dac8] rounded px-3 py-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={(draft.eventSlugs || []).includes(event.slug)}
                          onChange={() => toggleEvent(event.slug)}
                        />
                        <span>
                          <span className="block text-sm text-[#1e231f] font-medium">{event.name || event.slug}</span>
                          <span className="block text-[11px] text-[#9a876f]">{event.slug}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {events.length === 0 && <p className="text-sm text-[#9a876f]">No events are available.</p>}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                <Field label="Starts (optional)">
                  <input
                    type="datetime-local"
                    className={inputCls}
                    value={toLocalDateTime(draft?.startsAt)}
                    onChange={(e) => set("startsAt", fromLocalDateTime(e.target.value))}
                  />
                </Field>
                <Field label="Expires (optional)">
                  <input
                    type="datetime-local"
                    className={inputCls}
                    value={toLocalDateTime(draft?.expiresAt)}
                    onChange={(e) => set("expiresAt", fromLocalDateTime(e.target.value))}
                  />
                </Field>
                <Field label="Usage limit (optional)" hint="Leave blank for unlimited uses.">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className={inputCls}
                    value={draft?.usageLimit ?? ""}
                    placeholder="Unlimited"
                    onChange={(e) => set("usageLimit", e.target.value)}
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Receipt / checkout label (optional)" hint="If blank, checkout creates a label from the code and value.">
                  <input
                    className={inputCls}
                    value={draft?.label || ""}
                    placeholder="e.g. Launch offer"
                    maxLength={160}
                    onChange={(e) => set("label", e.target.value)}
                  />
                </Field>
              </div>

              {msg && (
                <p className="mt-5 text-[#9b3d0a] text-sm bg-[#f3e7d6] border border-[#e0c9ad] rounded px-3 py-2">
                  {msg}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-3 mt-6 pt-5 border-t border-[#eee3d2]">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-[#9b3d0a] hover:bg-[#7e3208] disabled:opacity-50 text-white px-6 py-3 rounded-full text-sm font-semibold"
                >
                  <Save size={16} /> {saving ? "Saving…" : editingId ? "Save changes" : "Create discount"}
                </button>
                <button
                  type="button"
                  onClick={() => { setView("list"); setDraft(null); setMsg(""); }}
                  className="text-[#6e5646] text-sm font-semibold px-3 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <XCircle size={26} className="text-[#9b3d0a] mb-3" />
            <h3 className="feral-serif text-[#9b3d0a] text-2xl">Delete {deleteTarget.code}?</h3>
            <p className="text-[#6e5646] text-sm leading-relaxed mt-2">
              This removes the code from future checkouts. Existing paid orders keep their recorded discount details.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-full text-sm font-semibold text-[#6e5646] hover:bg-[#f3e7d6]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-5 py-2 rounded-full text-sm font-semibold"
              >
                <Trash2 size={15} /> {deleting ? "Deleting…" : "Delete discount"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDiscounts;
